import { ReactNode } from "react";
import ArrowForwardRoundedIcon from "@mui/icons-material/ArrowForwardRounded";
import EditRoundedIcon from "@mui/icons-material/EditRounded";
import { Button } from "../Button";
import { VISUALLY_HIDDEN } from "../visuallyHidden";
import s from "./ChangeDiff.module.scss";

type ValuesProps = {
  was?: ReactNode;
  now?: ReactNode;
  /** Hidden word read before the old value ("was"). Leave it out where "changed to" already says it. */
  wasPrefix?: string;
  /** Hidden words read in place of the arrow ("changed to"). */
  changedToLabel: string;
  /** Shown (not struck through) in place of a missing old value, e.g. "empty". */
  emptyLabel?: string;
};

/**
 * The one "was → now" presentation: the old value muted and struck through, an arrow, the new value highlighted.
 * Never colour alone: a screen reader hears "was …", "changed to …". Values are the caller's (wrap user text in <bdi>).
 */
export const ChangeValues = ({ was, now, wasPrefix, changedToLabel, emptyLabel }: ValuesProps) => {
  const hasWas = was !== undefined && was !== null && was !== "";
  const hasNow = now !== undefined && now !== null && now !== "";
  const old = hasWas ? <span className={s.old}>{was}</span> : emptyLabel ? <span className={s.empty}>{emptyLabel}</span> : null;
  return (
    <>
      {old && wasPrefix && <span className={VISUALLY_HIDDEN}>{wasPrefix} </span>}
      {old}
      {old && hasNow && <>{" "}<span className={VISUALLY_HIDDEN}>{changedToLabel}</span><ArrowForwardRoundedIcon aria-hidden className={s.arrow} />{" "}</>}
      {hasNow && <span className={s.new}>{now}</span>}
    </>
  );
};

type Props = {
  label: string;
  was?: ReactNode;
  now: ReactNode;
  wasLabel: string;
  changedToLabel: string;
  emptyLabel: string;
  /** A word or pill after the label (e.g. "editing"). */
  status?: ReactNode;
  onEdit?: () => void;
  /** The Edit button's word; its name adds the label ("Edit House phone"). */
  editLabel?: string;
  /** An inline editor: shown under the old value, in place of the new value and the Edit button. */
  children?: ReactNode;
};

/** One field of a suggested change (boards 34–38): label, "was" struck through, the new value, Edit. */
export const ChangeDiff = ({ label, was, now, wasLabel, changedToLabel, emptyLabel, status, onEdit, editLabel, children }: Props) => {
  const editing = children !== undefined && children !== null && children !== false;
  return (
    <div className={editing ? `${s.diff} ${s.editing}` : s.diff}>
      <div className={s.content}>
        <span className={s.label}>{label}{status && <span className={s.status}>{status}</span>}</span>
        <span className={s.values}>
          {editing
            ? <ChangeValues was={was} wasPrefix={wasLabel} changedToLabel={changedToLabel} emptyLabel={emptyLabel} />
            : <ChangeValues was={was} now={now} wasPrefix={wasLabel} changedToLabel={changedToLabel} emptyLabel={emptyLabel} />}
        </span>
        {editing && children}
      </div>
      {!editing && onEdit && editLabel && (
        <Button variant="ghost" icon={<EditRoundedIcon />} onClick={onEdit} className={s.edit}>
          {editLabel}<span className={VISUALLY_HIDDEN}> {label}</span>
        </Button>
      )}
    </div>
  );
};
